import { Box, Stack } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { createPortal } from 'react-dom';

import { _deleteVerification, _postVerifications } from '@/api/entity/mutations';
import { ENTITY_QUERY_KEYS } from '@/api/entity/queries';
import { _postFiles, FileMetadata } from '@/api/fund/mutations';
import ArrowRightIcon from '@/assets/svg/ui-elements/arrow-right.svg';
import DeleteIcon from '@/assets/svg/ui-elements/delete-outline.svg';
import LinkIcon from '@/assets/svg/ui-elements/link.svg';
import PlusIcon from '@/assets/svg/ui-elements/plus.svg';
import {
  Button,
  Dropzone,
  FileItem,
  IconButton,
  Input,
  Modal,
  theme,
  Typography,
} from '@/design-system';
import { useIsMobile } from '@/global-hooks/use-is-mobile';
import { useModal } from '@/global-hooks/use-modal';
import { useFilesById } from '@/shared/hooks/use-files-by-id';
import { useEntitySlice } from '@/stores/selectors';
import { uploadToS3 } from '@/utils/upload-to-s3';

import { getRequiredUploadSections, titleToTypeMap, VerificationSection } from '../common';
import {
  FieldGrid,
  LinksContainer,
  ModalOptionButton,
  PanelHeader,
  SectionHeading,
  StepSection,
  VerificationPanel,
} from '../styled';
import { useEntityReviewMode } from '../use-entity-review-mode';
import { useEntityWriteAccess } from '../use-entity-write-access';
import { getStepFieldErrors } from '../validate-step';
import { useWizardTitleSlot } from '../wizard-title-slot';
import { VerificationFileCard } from './verification-file-card';
import { UpdatedBadge } from './wizard-status-badges';

const URL_PATTERN =
  /^(https?:\/\/)?(www\.)?[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}(\/.*)?$/;

export const UploadSection = ({ title, required }: { title: string; required: boolean }) => {
  const { stepForms, validatedSteps, currentStep } = useEntitySlice();
  const { forms, isReviewMode } = useEntityReviewMode();
  const { canWriteVerifications } = useEntityWriteAccess();
  const [uploading, setUploading] = useState(false);
  const [uploadingName, setUploadingName] = useState<string | null>(null);
  const verificationsDocuments = forms?.verifications.filter((el) => el.format === 'file') ?? [];
  const verificationsLinks = forms?.verifications.filter((el) => el.format === 'url') ?? [];

  const canEditVerifications = canWriteVerifications && !isReviewMode;

  const isStepValidated = validatedSteps.includes(currentStep);
  const fieldErrors = getStepFieldErrors(currentStep, stepForms);
  const expectedType = titleToTypeMap[title];
  const fieldKey = `verifications.${expectedType}`;

  const isValid = isReviewMode || !required || !isStepValidated || !fieldErrors[fieldKey];
  const queryClient = useQueryClient();

  const { mutate: postFiles } = useMutation({ mutationFn: _postFiles });
  const { mutate: postVerifications } = useMutation({ mutationFn: _postVerifications });
  const { mutate: deleteVerification } = useMutation({ mutationFn: _deleteVerification });

  const files = verificationsDocuments.filter((file) => file.type === titleToTypeMap[title]);
  const links = verificationsLinks.filter((link) => link.type === titleToTypeMap[title]);

  const filesById = useFilesById(files.map((file) => file.fileId));

  const [newLink, setNewLink] = useState('');

  const handleFileSelect = async (selected: File[]) => {
    const file = selected[0];
    if (!file) return;

    setUploading(true);
    setUploadingName(file.name);

    const metadata = {
      filename: file.name,
      contentType: file.type,
      category: 'document',
      ownerType: 'entity',
      ownerId: stepForms.id,
      file,
    } as FileMetadata & { file: File };

    postFiles(metadata, {
      onSuccess: async (data) => {
        try {
          await uploadToS3({
            uploadUrl: data.uploadUrl,
            uploadHeaders: data.uploadHeaders as Record<string, string>,
            file,
            fileId: data.fileId,
            compress: true,
          });

          const newDoc = {
            fileId: data.fileId,
            type: titleToTypeMap[title],
            format: 'file' as const,
            fileName: file.name,
            fileSize: file.size,
          };

          postVerifications(
            { id: stepForms.id, ...newDoc },
            {
              onSuccess: () => {
                queryClient.invalidateQueries({
                  queryKey: [ENTITY_QUERY_KEYS.entities, stepForms.id],
                });
              },
              onError: () => {
                setUploading(false);
              },
              onSettled: () => {
                setUploading(false);
              },
            },
          );
        } catch (err) {
          console.error('File upload error', err);
          setUploading(false);
        }
      },
      onError: () => {
        setUploading(false);
      },
    });
  };

  const handleRemove = (docId: string) => {
    deleteVerification(
      { entityId: stepForms.id, verificationId: docId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [ENTITY_QUERY_KEYS.entities, stepForms.id],
          });
        },
      },
    );
  };

  const isLinkValid = URL_PATTERN.test(newLink.trim());

  const handleSubmitLink = () => {
    const trimmed = newLink.trim();
    if (!trimmed || !isLinkValid) return;

    postVerifications(
      { id: stepForms.id, url: trimmed, type: titleToTypeMap[title], format: 'url' as const },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [ENTITY_QUERY_KEYS.entities, stepForms.id],
          });
          setNewLink('');
        },
      },
    );
  };

  return (
    <VerificationPanel $invalid={!isValid}>
      <PanelHeader>
        <Typography variant="h6">{title}</Typography>

        {required && (
          <Typography variant="body3" color={theme.colors.error.solid}>
            Required
          </Typography>
        )}
      </PanelHeader>

      {!!files.length && (
        <Stack flexDirection="column" gap={`${theme.spacing[8]}px`}>
          {files.map((file) => {
            const meta = file.fileId ? filesById[file.fileId] : undefined;
            return (
              <VerificationFileCard
                key={file.id}
                file={file}
                url={meta?.url}
                onRemove={canEditVerifications ? handleRemove : undefined}
              />
            );
          })}
        </Stack>
      )}

      {!isReviewMode &&
        (uploading ? (
          <FileItem
            file={{
              id: 'uploading-verification',
              name: uploadingName ?? '',
              contentType: 'application/pdf',
              status: 'uploading',
              progress: null,
            }}
          />
        ) : (
          <Dropzone
            subject="file"
            accept=".pdf,application/pdf"
            acceptLabel="PDF"
            disabled={!canWriteVerifications}
            onFilesSelected={handleFileSelect}
          />
        ))}

      {!!links.length && (
        <Stack flexDirection="column" gap={`${theme.spacing[8]}px`}>
          {links.map((link) => (
            <LinksContainer key={link.id}>
              <Typography variant="body2" noWrap>
                {link.url}
              </Typography>
              {!isReviewMode && (
                <IconButton
                  aria-label={`Remove link ${link.url}`}
                  variant="outline"
                  color="destructive"
                  size="xs"
                  disabled={!canWriteVerifications}
                  onClick={() => handleRemove(link.id)}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </LinksContainer>
          ))}
        </Stack>
      )}

      {!isReviewMode && (
        <Input
          size="lg"
          label="Link"
          aria-label={`Link to ${title.toLowerCase()}`}
          placeholder={`Paste link to ${title.toLowerCase()}`}
          value={newLink}
          disabled={!canWriteVerifications}
          startIcon={<LinkIcon />}
          state={newLink.trim() && !isLinkValid ? 'destructive' : 'default'}
          helperText={newLink.trim() && !isLinkValid ? 'Please enter a valid URL' : ''}
          onChange={(e) => setNewLink(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSubmitLink();
            }
          }}
          endControl={
            <Button
              type="button"
              variant="ghost"
              color="primary"
              size="sm"
              disabled={!newLink.trim() || !isLinkValid || !canWriteVerifications}
              onClick={handleSubmitLink}
            >
              Add
            </Button>
          }
        />
      )}
    </VerificationPanel>
  );
};

const proofOfVerificationOptions: string[] = [
  'Articles or equivalent',
  'Certificate of good standing',
  'Certificate of incumbency',
];

export const Verification = () => {
  const { open, handleOpen, handleClose } = useModal();
  const isMobile = useIsMobile();
  const titleSlot = useWizardTitleSlot();
  const [selectedTitles, setSelectedTitles] = useState<string[]>([]);
  const { canWriteVerifications } = useEntityWriteAccess();
  const { forms, isChangedUnder, highlightIf, isReviewMode } = useEntityReviewMode();
  const isVerificationsChanged = isChangedUnder('verifications');

  const requiredSections = getRequiredUploadSections(forms.registeredAt);
  const requiredTitles = requiredSections.map((s) => s.title);

  const uploadedTitles = [
    ...new Set(
      (forms?.verifications ?? [])
        .map((v) => Object.keys(titleToTypeMap).find((key) => titleToTypeMap[key] === v.type))
        .filter((title): title is string => !!title),
    ),
  ];

  const optionalTitles = [...new Set([...uploadedTitles, ...selectedTitles])].filter(
    (title) => !requiredTitles.includes(title),
  );

  const sections: VerificationSection[] = [
    ...requiredSections,
    ...optionalTitles.map((title) => ({ title, required: false })),
  ];

  const addedTitles = sections.map((s) => s.title);
  const remainingOptions = proofOfVerificationOptions.filter((opt) => !addedTitles.includes(opt));

  const handleSelect = (title: string) => {
    setSelectedTitles((prev) => [...prev, title]);
    handleClose();
  };

  const addProofButton = (
    <Button size="md" disabled={!canWriteVerifications || isReviewMode} onClick={handleOpen}>
      <PlusIcon />
      Additional proof of verification
    </Button>
  );

  return (
    <StepSection $changed={highlightIf(isVerificationsChanged)}>
      {isVerificationsChanged && (
        <SectionHeading>
          <UpdatedBadge />
        </SectionHeading>
      )}

      {remainingOptions.length > 0 && titleSlot && createPortal(addProofButton, titleSlot)}

      <FieldGrid $columns={isMobile ? 1 : 2}>
        {sections.map((section) => (
          <UploadSection key={section.title} title={section.title} required={section.required} />
        ))}
      </FieldGrid>

      <Modal
        open={open}
        onOpenChange={(next) => (next ? handleOpen() : handleClose())}
        showClose
        maxWidth={504}
        title="Select Proof of Verification"
      >
        <Stack flexDirection="column" gap={`${theme.spacing[8]}px`} width="100%">
          {remainingOptions.map((title) => (
            <ModalOptionButton
              key={title}
              variant="ghost"
              color="tertiary"
              size="lg"
              fullWidth
              onClick={() => handleSelect(title)}
            >
              <Box width="100%" textAlign="left">
                {title}
              </Box>
              <ArrowRightIcon width={16} height={16} />
            </ModalOptionButton>
          ))}
        </Stack>
      </Modal>
    </StepSection>
  );
};
